import { type FC, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import * as CSS from "./mention-menu.styles";
import type { MentionSection } from "./mentionables";
import type { MentionMenuPosition } from "./use-mention-autocomplete";

interface MentionMenuProps {
	sections: MentionSection[];
	selectedIndex: number;
	position: MentionMenuPosition;
	onHover: (index: number) => void;
	onSelect: (index: number) => void;
}

const MentionMenu: FC<MentionMenuProps> = ({
	sections,
	selectedIndex,
	position,
	onHover,
	onSelect,
}) => {
	const menuRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		menuRef.current
			?.querySelector(`[data-index="${selectedIndex}"]`)
			?.scrollIntoView({ block: "nearest" });
	}, [selectedIndex]);

	let runningIndex = -1;

	return createPortal(
		<CSS.Menu
			ref={menuRef}
			style={{ left: position.left, bottom: position.bottom }}
			onMouseDown={(e) => e.preventDefault()}
		>
			{sections.map((section) => (
				<CSS.Section key={section.teamName}>
					<CSS.SectionLabel>{section.teamName}</CSS.SectionLabel>
					{section.items.map((item) => {
						runningIndex += 1;
						const index = runningIndex;

						return (
							<CSS.Item
								key={item.id}
								type="button"
								$active={index === selectedIndex}
								data-index={index}
								onMouseEnter={() => onHover(index)}
								onClick={() => onSelect(index)}
							>
								{item.colour && (
									<CSS.Swatch style={{ backgroundColor: item.colour }} />
								)}
								<CSS.Name>{item.fullName}</CSS.Name>
								<CSS.Meta>
									{item.role}
									{item.number !== undefined ? ` · #${item.number}` : ""}
								</CSS.Meta>
							</CSS.Item>
						);
					})}
				</CSS.Section>
			))}
		</CSS.Menu>,
		document.body,
	);
};

export default MentionMenu;
